import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { taskKeys } from '@/features/tasks/useTasks';
import type { AiSettings, Braindump, BraindumpItem, BraindumpSource } from './types';

const DUMPS = ['braindumps'] as const;
const AI = ['ai', 'settings'] as const;

export function useAiSettings() {
  return useQuery<AiSettings>({
    queryKey: AI,
    queryFn: () => api<AiSettings>('/api/ai/settings'),
    // Always re-read on mount. This one query gates the whole braindump
    // feature, so serving it from a stale cache tells the user they have no
    // key when they just saved one. It is a single cheap request.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation<AiSettings, ApiError, Partial<AiSettings> & { api_key?: string }>({
    mutationFn: (body) => api<AiSettings>('/api/ai/settings', { method: 'PATCH', json: body }),
    onSuccess: (data) => {
      qc.setQueryData(AI, data);
      // Seeding the cache is not enough on its own: another tab, or the same
      // settings saved from elsewhere, would leave other mounted readers
      // holding the old value.
      qc.invalidateQueries({ queryKey: AI });
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useBraindumps(source?: BraindumpSource) {
  return useQuery<Braindump[]>({
    // The unfiltered list is its own cache entry, so the nav badge and a
    // filtered page do not fight over one key.
    queryKey: source ? ([...DUMPS, source] as const) : DUMPS,
    queryFn: () =>
      api<Braindump[]>(source ? `/api/braindump?source=${source}` : '/api/braindump'),
  });
}

/**
 * How many proposals are still waiting on a decision.
 *
 * Drives the nav marker. Deliberately derived from the list the Inbox already
 * loads rather than a second endpoint, one unfinished decision is all the
 * marker needs to know about.
 */
export function usePendingReviewCount(): number {
  const dumps = useBraindumps();
  return (dumps.data ?? []).reduce(
    (total, dump) => total + dump.items.filter((i) => i.state === 'proposed').length,
    0,
  );
}

function useSettle() {
  const qc = useQueryClient();
  return () => {
    // Prefix match: clears the unfiltered list and every source-filtered one.
    qc.invalidateQueries({ queryKey: DUMPS });
    qc.invalidateQueries({ queryKey: AI });
    qc.invalidateQueries({ queryKey: taskKeys.all });
  };
}

export function useCreateBraindump() {
  const settle = useSettle();
  return useMutation<Braindump, ApiError, string>({
    mutationFn: (text) => api<Braindump>('/api/braindump', { method: 'POST', json: { text } }),
    onSuccess: settle,
    onError: (error) => toast.error(error.message),
  });
}

export function useUpdateItem() {
  const settle = useSettle();
  return useMutation<BraindumpItem, ApiError, { id: number } & Partial<BraindumpItem>>({
    mutationFn: ({ id, ...patch }) =>
      api<BraindumpItem>(`/api/braindump/items/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: settle,
    onError: (error) => toast.error(error.message),
  });
}

export function useDecideItems() {
  const settle = useSettle();

  const accept = useMutation<Braindump, ApiError, { dumpId: number; itemIds?: number[] }>({
    mutationFn: ({ dumpId, itemIds }) =>
      api<Braindump>(`/api/braindump/${dumpId}/accept`, {
        method: 'POST',
        json: { item_ids: itemIds ?? null },
      }),
    onSuccess: settle,
    onError: (error) => toast.error(error.message),
  });

  const reject = useMutation<Braindump, ApiError, { dumpId: number; itemIds?: number[] }>({
    mutationFn: ({ dumpId, itemIds }) =>
      api<Braindump>(`/api/braindump/${dumpId}/reject`, {
        method: 'POST',
        json: { item_ids: itemIds ?? null },
      }),
    onSuccess: settle,
    onError: (error) => toast.error(error.message),
  });

  const retry = useMutation<Braindump, ApiError, number>({
    mutationFn: (dumpId) => api<Braindump>(`/api/braindump/${dumpId}/retry`, { method: 'POST' }),
    onSuccess: settle,
    onError: (error) => toast.error(error.message),
  });

  const discard = useMutation<void, ApiError, number>({
    mutationFn: (dumpId) => api<void>(`/api/braindump/${dumpId}`, { method: 'DELETE' }),
    onSuccess: settle,
    onError: (error) => toast.error(error.message),
  });

  return { accept, reject, retry, discard };
}
